import React, { useEffect, useMemo, useState } from "react";
import styles from "./styles.module.scss";
import { ReactComponent as UserIcon } from "../../../assets/svg/loginemail.svg";
import { useAuth } from "../../../hooks/useLogin";
import AuthLayout from "./components/AuthLayout";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import CommonInputWrapper from "src/modules/molecules/CommonInputWrapper";
import {
  REACT_AUTH_ENDPOINT,
  USER_DOMAIN,
  XSM_BASE_URL,
  CLIENT_URL,
} from "src/constants/auth";
import { useSSOAuthorization } from "../../../hooks/useSSOAuthorization";
import { CircularProgress } from "@mui/material";
import { getCookie } from "src/utils";

// XSM-1536: SSO Login
const Login = () => {
  const { isLoading, setState, state } = useAuth();
  const [searchParams] = useSearchParams();

  // Memoize auth state
  const authState = useMemo(
    () => ({
      sessionDataKey: searchParams.get("sessionDataKey"),
      code: searchParams.get("code"),
      error: searchParams.get("error"),
    }),
    [searchParams]
  );

  // Use custom hook
  const { hasAuthorizationCode } = useSSOAuthorization({
    config: {
      clientId: CLIENT_URL,
      redirectURL: REACT_AUTH_ENDPOINT,
      xsmDomain: USER_DOMAIN,
    },
    authState,
  });

  const isAuthenticated = () => {
    const isLoggedIn = localStorage.getItem("dm_isLoggedIn");
    return isLoggedIn === "true";
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState({ ...state, [e.target.name]: e.target.value });
  };

  // Check for `commonAuthId` cookie. Show loader while polling for it.
  const [checkingCommonAuth, setCheckingCommonAuth] = useState<boolean>(true);
  const [commonAuthFound, setCommonAuthFound] = useState<boolean>(false);

  useEffect(() => {
    let mounted = true;
    const start = Date.now();
    const timeoutMs = 3000; // give the cookie up to 3s to appear
    const interval = window.setInterval(() => {
      const cid = getCookie("commonAuthId");
      if (cid) {
        if (mounted) setCommonAuthFound(true);
        clearInterval(interval);
        if (mounted) setCheckingCommonAuth(false);
        return;
      }

      if (Date.now() - start > timeoutMs) {
        // didn't get cookie within timeout — stop checking and show SSOLogin
        if (mounted) setCheckingCommonAuth(false);
        clearInterval(interval);
      }
    }, 200);

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, []);

  if (checkingCommonAuth || commonAuthFound) {
    return <div className={styles.loadingContainer}>Loading...</div>;
  }

  return (
    <>
      {!isAuthenticated() ? (
        <AuthLayout>
          <form
            method="POST"
            action={`${XSM_BASE_URL}/xsm-api-sso/v2/proxy/commonauth?sessionDataKey=${encodeURIComponent(
              authState.sessionDataKey || ""
            )}&XSM-Domain=${USER_DOMAIN}&redirect_uri=${REACT_AUTH_ENDPOINT}`}
            onSubmit={() => {
              // Mark explicit login so the success toast can show after token exchange.
              // Silent SSO / product switching never submits this form.
              sessionStorage.setItem("xsm_show_login_toast", "1");
            }}
          >
            <div className={styles.inputContainer}>
              <input type="hidden" name="XSM-Domain" value={USER_DOMAIN} />
              <input
                type="hidden"
                name="sessionDataKey"
                value={authState.sessionDataKey || ""}
              />
              <input
                type="hidden"
                name="redirect_uri"
                value={REACT_AUTH_ENDPOINT as string}
              />
              <CommonInputWrapper
                {...{
                  label: "Email",
                  name: "username",
                  placeholder: "Enter email",
                  startIcon: <UserIcon />,
                  onChange: handleChange,
                }}
              />
              <CommonInputWrapper
                {...{
                  label: "Password",
                  name: "password",
                  placeholder: "Enter password",
                  type: "password",
                  onChange: handleChange,
                }}
              />
            </div>
            <div className={styles.bottomContainer}>
              <Link to={"/forgot-password"} className={styles.forgotPassword}>
                Forgot Password?
              </Link>
              <button
                id="sign-in-btn"
                disabled={
                  !(state.username && state.password) || hasAuthorizationCode
                }
                className={`${styles.signinBtn} ${isLoading || hasAuthorizationCode ? styles.loadingBtn : ""
                  }`}
                style={{ border: "none", cursor: "pointer" }}
                type="submit"
              >
                {isLoading || hasAuthorizationCode ? (
                  <CircularProgress
                    sx={{
                      width: "20px !important",
                      height: "20px !important",
                      color: "#fff",
                    }}
                  />
                ) : (
                  "Sign In"
                )}
              </button>
            </div>
          </form>
        </AuthLayout>
      ) : (
        <Navigate to="/" replace />
      )}
    </>
  );
};

export default Login;
